import React from 'react';
import { Card } from './ui/card';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';

const CustomizedWellnessPrograms = () => {
  const navigate = useNavigate();
  const handleBookAppointment = () => navigate('/book-appointment');

  const services = [
    "Personalized assessments",
    "Integrated therapies",
    "Continuous support",
    "Nutrition & lifestyle guidance",
  ];

  const benefits = [
    "Tailored plans for individual goals",
    "Support from certified health professionals",
    "Flexible and adaptable programs",
    "Focus on long-term wellness, not quick fixes",
  ];

  return (
    <>
      <Header hideBookButton={true} />
      <section className="py-20 bg-gradient-to-br from-white via-slate-50 to-blue-50 relative overflow-hidden min-h-screen">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#204080]/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-[#204080]/10 text-[#204080] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span>Customized Wellness Programs</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-[#204080] mb-6 font-serif">
              Your Health, Your Way
              <span className="block bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
                Personalized & Holistic
              </span>
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Achieve your health goals with personalized wellness plans that blend fitness, nutrition, and therapy into a holistic approach.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-16 max-w-3xl mx-auto">
            {services.map((service, idx) => (
              <Card
                key={idx}
                className="flex items-center gap-4 p-6 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <CheckCircle size={24} className="text-[#204080]" />
                <p className="text-gray-700 font-medium">{service}</p>
              </Card>
            ))}
          </div>

          {/* Benefits Section */}
          <Card className="max-w-3xl mx-auto bg-blue-50 p-8 rounded-lg mb-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Why Choose Our Programs?</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 text-lg">
              {benefits.map((benefit, idx) => (
                <li key={idx}>{benefit}</li>
              ))}
            </ul>
          </Card>

          {/* Success Metrics */}
          <Card className="p-8 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 shadow-2xl mb-16 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-4">Proven Wellness Impact</h3>
              <p className="text-blue-100 text-lg">Helping you achieve lasting health improvements</p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">95%</div>
                <div className="text-blue-100 text-sm">Program Completion Rate</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">85%</div>
                <div className="text-blue-100 text-sm">Improved Physical Health</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">90%</div>
                <div className="text-blue-100 text-sm">Increased Mental Well-being</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">1000+</div>
                <div className="text-blue-100 text-sm">Clients Served</div>
              </div>
            </div>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Card className="max-w-4xl mx-auto p-8 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-2xl">
              <h3 className="text-2xl font-bold text-[#204080] mb-4">
                Ready to Start Your Personalized Wellness Plan?
              </h3>
              <p className="text-gray-600 mb-8 text-lg">
                Let us tailor a wellness journey just for you.
              </p>
              <div className="flex justify-center">
                <button
                  className="flex items-center justify-center space-x-2 px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  onClick={() => navigate('/book-appointment', { state: { selectedService: 'Wellness Program' } })}
                >
                  <span>Book Wellness Program</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
};

export default CustomizedWellnessPrograms;
